<script setup>
import { ref, onMounted, defineProps } from 'vue'
import axios from 'axios'
import mdtoast from '@dmuy/toast'
import '@dmuy/toast/dist/mdtoast.css'
import { Carousel, Slide } from 'vue3-carousel'
import 'vue3-carousel/dist/carousel.css'

const props = defineProps({
  locationId: String
})

const moments = ref([])
const momentsLoading = ref(true)
const momentStatsLoading = ref(true)
const momentStats = ref({})

const getMoments = () => {
  axios.get(`/ajax/locations/${props.locationId}/moments`).then((response) => {
    moments.value = response.data
    momentsLoading.value = false
  }).catch((error) => {
    mdtoast.error(error, { duration: 10000 })
  })
}

const getMomentStats = () => {
  axios.get(`/ajax/locations/${props.locationId}/moments/stats`).then((response) => {
    momentStats.value = response.data
    momentStatsLoading.value = false
  }).catch((error) => {
    mdtoast.error(error, { duration: 10000 })
  })
}

const noMoments = () => Object.keys(moments.value).length === 0

onMounted(() => {
  getMoments()
  getMomentStats()
})
</script>

<template>
  <div class="px-4 py-6">
    <div v-if="momentsLoading">
      <div role="status">
        <svg
          aria-hidden="true"
          class="mr-auto ml-auto w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <!-- eslint-disable max-len -->
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor" />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill" />
          <!-- eslint-enable max-len -->
        </svg>
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <div
      v-else
      class="card grid grid-cols-1 md:grid-cols-3 m-8 px-4 border border-gray-200">
      <div class="col-span-1">
        <h3 class="p-6">
          Moments
        </h3>
        <div class="px-6">
          <dl>
            <dt class="mb-2 font-semibold leading-none text-gray-900 dark:text-white">
              Last 7
            </dt>
            <dd class="mb-4 text-gray-500 dark:text-gray-400 sm:mb-5">
              <div
                v-if="momentStatsLoading"
                role="status"
                class="space-y-2.5 animate-pulse max-w-lg">
                <div class="flex items-center w-full space-x-2">
                  <div class="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-32" />
                </div>
                <span class="sr-only">Loading...</span>
              </div>
              <p
                v-else
                class="text-3xl font-bold">
                {{ momentStats['lastWeek'] }}
              </p>
            </dd>
            <dt class="mb-2 font-semibold leading-none text-gray-900 dark:text-white">
              Last 30
            </dt>
            <dd class="mb-4 text-gray-500 dark:text-gray-400 sm:mb-5">
              <div
                v-if="momentStatsLoading"
                role="status"
                class="space-y-2.5 animate-pulse max-w-lg">
                <div class="flex items-center w-full space-x-2">
                  <div class="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-32" />
                </div>
                <span class="sr-only">Loading...</span>
              </div>
              <p
                v-else
                class="text-3xl font-bold">
                {{ momentStats['lastMonth'] }}
              </p>
            </dd>
            <dt class="mb-2 font-semibold leading-none text-gray-900 dark:text-white">
              Last 90
            </dt>
            <dd class="mb-4 text-gray-500 dark:text-gray-400 sm:mb-5">
              <div
                v-if="momentStatsLoading"
                role="status"
                class="space-y-2.5 animate-pulse max-w-lg">
                <div class="flex items-center w-full space-x-2">
                  <div class="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-32" />
                </div>
                <span class="sr-only">Loading...</span>
              </div>
              <p
                v-else
                class="text-3xl font-bold">
                {{ momentStats['lastQuarter'] }}
              </p>
            </dd>
          </dl>
        </div>
      </div>

      <div class="col-span-2">
        <section
          v-if="noMoments()"
          class="bg-white dark:bg-gray-900">
          <div class="py-8 px-4 mx-auto max-w-screen-xl lg:py-16">
            <h1 class="mb-4 text-4xl font-extrabold tracking-tight leading-none text-gray-900 md:text-5xl lg:text-6xl dark:text-white">
              Start creating Moments
            </h1>
            <p class="mb-8 text-lg font-normal text-gray-500 lg:text-xl dark:text-gray-400">
              Unlock the power of local, fresh content with Growth Booster.
            </p>
            <div class="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
              <a
                href="https://help.linda.co"
                target="_blank"
                class="btn btn-white btn-large font-medium">
                Learn more
              </a>
            </div>
          </div>
        </section>
        <div
          v-else
          class="scroll hor whitespace-nowrap pb-4">
          <div
            v-for="moment in moments"
            :key="moment.id"
            class="inline-block px-8 text-center align-top">
            <div class="max-w-sm rounded shadow-lg pb-6">
              <Carousel v-if="moment.images && moment.images.length">
                <Slide
                  v-for="image in moment.images"
                  :key="image.fullUrl">
                  <img
                    class="w-80 h-80 object-contain"
                    :src="image.fullUrl">
                </Slide>
              </Carousel>
              <p class="whitespace-normal text-xs m-4 p-4">
                {{ moment.description }}
              </p>
              <p class="pt-2 pb-4">
                {{ moment.administrativeArea && moment.administrativeArea.name }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
